/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useState } from 'react';
import { Modal } from '../../shared/modals/Modal';
import { ActivityIndicator, Pressable, StyleSheet, Text, TouchableOpacity, useWindowDimensions, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { colors } from '../../shared/theme/colors'
import { spacing } from '../../shared/theme/spacing';
import { ModalScreenHeader } from '../../shared/modals/ModalScreenHeader'
import { modalExperience } from '../../shared/modals/modalExperience.styles';
import { posSharedUi as posUi } from '../../shared/ui/posSharedUi';
import { createCustomer } from '../../customers/infra/customerAccounts';
import { useModalBackdropPadding } from '../../shared/ui/hooks-ui/useModalBackdropPadding';

export type FiscalCustomerCreateSheetProps = {
  visible: boolean;
  padron: { cuit: string; razonSocial: string; taxCondition: number | null } | null;
  onClose: () => void;
  onLinked: (customerId: string) => void;
};

export function FiscalCustomerCreateSheet({
  visible,
  padron,
  onClose,
  onLinked,
}: FiscalCustomerCreateSheetProps) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const modalBackdropPadding = useModalBackdropPadding();
  const { width } = useWindowDimensions();
  const isMobile = width < 768;
  const insets = useSafeAreaInsets();

  const handleConfirm = async () => {
    if (!padron?.razonSocial.trim()) {
      setError('No hay datos de padrón para crear el cliente.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const customerId = await createCustomer({
        name: padron.razonSocial.trim(),
        cuit: padron.cuit.replace(/\D/g, '') || null,
        creditLimit: 0,
        phone: '',
      });
      onLinked(customerId);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'No se pudo crear el cliente.');
    } finally {
      setSaving(false);
    }
  };

  if (!padron) return null;

  return (
    <Modal visible={visible} transparent animationType={isMobile ? 'slide' : 'fade'} onRequestClose={onClose}>
        <Pressable style={[posUi.modalBackdrop, isMobile ? { justifyContent: 'flex-end' } : modalExperience.modalRoot, isMobile ? null : modalBackdropPadding]} onPress={isMobile ? onClose : undefined}>
          <Pressable
            style={[isMobile ? [styles.sheetCard, { paddingBottom: Math.max(insets.bottom, spacing.sm) }] : [posUi.modalCard, modalExperience.modalWidthStandard]]}
            onPress={(e) => e.stopPropagation()}
          >
            {isMobile ? <View style={styles.grabber} /> : null}
            <ModalScreenHeader title="¿Crear cliente?" onClose={onClose} />
          <View style={modalExperience.formPanel}>
            <Text style={posUi.checkoutHint}>
              Encontramos en ARCA: {padron.razonSocial} (CUIT {padron.cuit}). ¿Lo guardamos como cliente para
              crédito y futuras ventas?
            </Text>
            {error ? <Text style={{ color: colors.danger, marginTop: spacing.sm }}>{error}</Text> : null}
          </View>
          <View style={[posUi.modalActions, modalExperience.footerActions, { marginTop: spacing.sm }]}>
            <TouchableOpacity style={posUi.secondaryButton} onPress={onClose} disabled={saving}>
              <Text style={posUi.secondaryButtonText}>Omitir</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[posUi.primaryButton, saving && posUi.disabledCard]}
              onPress={() => void handleConfirm()}
              disabled={saving}
            >
              {saving ? (
                <ActivityIndicator color="#FFFFFF" />
              ) : (
                <Text style={posUi.primaryButtonText}>Crear cliente</Text>
              )}
            </TouchableOpacity>
          </View>
        </Pressable>
        </Pressable>
    </Modal>
  );
}

const styles = StyleSheet.create({
  sheetCard: {
    width: '100%',
    maxHeight: '92%',
    backgroundColor: colors.surface,
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    borderTopWidth: 1,
    borderColor: colors.border,
    paddingTop: 8,
  },
  grabber: {
    width: 36,
    height: 4,
    borderRadius: 2,
    backgroundColor: colors.border,
    alignSelf: 'center',
    marginBottom: spacing.xs,
  },
});
